import type { MarketingLocale } from './marketing-locale'

/**
 * Every marketing page that exists in both languages, as a pt-BR ↔ en pair.
 *
 * ONE list feeds four consumers that must never disagree: the nav/footer
 * language switcher, the reciprocal `hreflang` tags, the language-suggestion
 * banner, and `scripts/build-sitemap.ts`. A page added to only some of those
 * is the classic way a localized site ends up with a switcher that 404s or
 * an `hreflang` pointing at a page that doesn't exist — Google drops the
 * whole cluster when the annotations aren't reciprocal.
 *
 * The English slugs are readable English, not transliterated Portuguese:
 * `/recursos/funil` → `/en/features/client-pipeline`.
 */
export type MarketingPagePair = {
  'pt-BR': string
  en: string
}

export const MARKETING_PAGE_PAIRS: readonly MarketingPagePair[] = [
  { en: '/en', 'pt-BR': '/' },
  { en: '/en/features/client-pipeline', 'pt-BR': '/recursos/funil' },
  { en: '/en/features/project-work', 'pt-BR': '/recursos/trabalho' },
  { en: '/en/features/deliverables', 'pt-BR': '/recursos/entregaveis' },
  { en: '/en/features/finances', 'pt-BR': '/recursos/numeros' },
  { en: '/en/features/ai', 'pt-BR': '/recursos/ia' },
  { en: '/en/project-journey', 'pt-BR': '/jornada' },
  { en: '/en/privacy-policy', 'pt-BR': '/politica-de-privacidade' },
  { en: '/en/terms-of-use', 'pt-BR': '/termos-de-uso' },
] as const

/**
 * The counterpart of `path` in the other language, or `null` when the page
 * has no translation. Callers must handle `null` rather than falling back to
 * the other locale's home — silently dumping a visitor on the homepage when
 * they asked for "this page in English" is worse than not offering it.
 */
export const counterpartPath = (path: string, target: MarketingLocale): string | null => {
  const normalized = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
  const source: MarketingLocale = target === 'en' ? 'pt-BR' : 'en'
  const pair = MARKETING_PAGE_PAIRS.find((candidate) => candidate[source] === normalized)
  return pair ? pair[target] : null
}
